import { useState } from "react";

import { Form, Button, Input, InputNumber, Popover, Typography } from "antd";
import { FormInstance } from "antd/lib/form";
import { PlusCircle, XCircle, Percent } from "lucide-react";

import { InvoiceLineTaxPayload } from "../../../../../bitmaker/schemas/Billing/Invoice";
import { TaxRate } from "../../../../../bitmaker/schemas/Billing/TaxRate";

import { useTaxRates } from "../../../../../hooks/useTaxRates";

import { formatCurrency } from "../../../../../utils";

import { TaxRateSelectorContent } from "../../../TaxRateSelector";

const { TextArea } = Input;

interface InvoiceItemsListProps {
  form: FormInstance;
}

export const InvoiceItemsList: React.FC<InvoiceItemsListProps> = ({ form }) => {
  const language = Form.useWatch("language", form);
  const currency = Form.useWatch("currency", form);

  const currencyPrefix = currency === "PEN" ? "S/" : "$";

  const texts = {
    headerDetails: language === "es" ? "Detalles del Item" : "Item Details",
    headerPrice: language === "es" ? "Precio Unitario" : "Unit Price",
    headerQty: language === "es" ? "Cantidad" : "Quantity",
    headerTotal: language === "es" ? "Total Línea" : "Line Total",
  };

  const [visiblePopovers, setVisiblePopovers] = useState<
    Record<number, boolean>
  >({});

  const { rates, loadingTaxRates, fetchTaxRatesError } = useTaxRates();

  const noTaxRatesAvailable = rates.length === 0;
  const isTaxSelectorDisabled =
    loadingTaxRates || !!fetchTaxRatesError || noTaxRatesAvailable;

  const openPopover = (name: number) => {
    setVisiblePopovers((prev) => ({ ...prev, [name]: true }));
  };

  const closePopover = (name: number) => {
    setVisiblePopovers((prev) => ({ ...prev, [name]: false }));
  };

  const getAppliedTaxNames = (name: number): string[] => {
    const appliedTaxes: InvoiceLineTaxPayload[] =
      form.getFieldValue(["items", name, "appliedTaxes"]) || [];
    return appliedTaxes.map((tax) => tax.taxName);
  };

  const getTaxRateById = (
    rates: TaxRate[],
    id: number,
  ): TaxRate | undefined => {
    return rates.find((rate) => rate.id === id);
  };

  const handleTaxApply = (
    name: number,
    selectedTaxIds: number[],
    hidePopover: () => void,
  ) => {
    const newAppliedTaxes: InvoiceLineTaxPayload[] = selectedTaxIds
      .map((taxId) => {
        const rateData = getTaxRateById(rates, taxId);
        if (rateData) {
          return {
            taxRate: rateData.rate,
            taxName: rateData.name,
          } as InvoiceLineTaxPayload;
        }
        return null;
      })
      .filter((tax): tax is InvoiceLineTaxPayload => tax !== null);

    form.setFieldsValue({
      items: form
        .getFieldValue("items")
        .map((item: any, index: number) =>
          index === name ? { ...item, appliedTaxes: newAppliedTaxes } : item,
        ),
    });

    hidePopover();
  };

  return (
    <div className="col-span-12">
      <Form.List name="items">
        {(fields, { add, remove }) => (
          <>
            {fetchTaxRatesError && (
              <Typography.Text type="secondary" className="block mb-2 text-sm">
                Tax rates could not be loaded. You can continue without taxes
                for now.
              </Typography.Text>
            )}

            {noTaxRatesAvailable && !fetchTaxRatesError && (
              <Typography.Text type="secondary" className="block mb-2 text-sm">
                No tax rates are available. You can add taxes in{" "}
                <a href="/billing/settings/taxes" className="underline">
                  Billing → Settings → Tax Rates
                </a>
                .
              </Typography.Text>
            )}

            {/* Header Columns */}
            <div className="grid grid-cols-12 gap-2 pb-2 font-semibold text-green-600">
              <div className="col-span-4">{texts.headerDetails}</div>
              <div className="col-span-2 text-right">{texts.headerPrice}</div>
              <div className="col-span-2 text-right">{texts.headerQty}</div>
              <div className="col-span-2 text-right">{texts.headerTotal}</div>
              <div className="col-span-2"></div>
            </div>

            {/* Divider */}
            <div className="grid grid-cols-12">
              <div className="border-b border-dashed border-gray-400 mb-2 col-span-10"></div>
            </div>

            {/* Rows of Items */}
            {fields.map(({ key, name, ...restField }) => {
              const popoverVisible = visiblePopovers[name] || false;
              const currentTaxNames = getAppliedTaxNames(name);

              const currentTaxIds = currentTaxNames
                .map(
                  (taxName) => rates.find((rate) => rate.name === taxName)?.id,
                )
                .filter((id): id is number => id !== undefined);

              return (
                <div
                  key={key}
                  className="grid grid-cols-12 gap-2 items-start mb-2"
                >
                  {/* Item Details (Name + Description) */}
                  <div className="col-span-4 flex flex-col gap-1">
                    {/* Item Name */}
                    <Form.Item
                      {...restField}
                      name={[name, "itemName"]}
                      className="mb-0"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value || !value.trim()) {
                              return Promise.reject(new Error("Required"));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        className="w-full font-semibold"
                        placeholder="Item name"
                      />
                    </Form.Item>

                    {/* Item Description */}
                    <Form.Item
                      {...restField}
                      name={[name, "itemDescription"]}
                      className="mb-0"
                    >
                      <TextArea
                        className="w-full text-xs text-gray-500"
                        placeholder="Optional item description"
                        autoSize={{ minRows: 2, maxRows: 5 }}
                      />
                    </Form.Item>
                  </div>

                  {/* Unit Price */}
                  <Form.Item
                    {...restField}
                    name={[name, "unitPrice"]}
                    className="col-span-2 mb-0"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber
                      prefix={currencyPrefix}
                      controls={false}
                      className="w-full input-number-align-right pr-1"
                      placeholder="0.00"
                    />
                  </Form.Item>

                  {/* Quantity */}
                  <Form.Item
                    {...restField}
                    name={[name, "quantity"]}
                    className="col-span-2 mb-0"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber
                      min={1}
                      controls={false}
                      className="w-full input-number-align-right"
                      placeholder="1"
                    />
                  </Form.Item>

                  {/* Line Total (Calculated) */}
                  <div className="col-span-2 text-right font-semibold text-gray-700">
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const quantity =
                          form.getFieldValue(["items", name, "quantity"]) || 0;
                        const unitPrice =
                          form.getFieldValue(["items", name, "unitPrice"]) || 0;
                        const lineTotalValue = quantity * unitPrice;
                        return (
                          <>
                            {formatCurrency(lineTotalValue, currency, language)}
                          </>
                        );
                      }}
                    </Form.Item>
                  </div>

                  {/* Add Taxes + Remove button */}
                  <div className="col-span-2 flex justify-center gap-1">
                    {/* Add taxes */}
                    <Popover
                      title="Add Taxes to Item"
                      trigger={isTaxSelectorDisabled ? [] : "click"}
                      placement="bottom"
                      open={isTaxSelectorDisabled ? false : popoverVisible}
                      onOpenChange={(visible) => {
                        if (isTaxSelectorDisabled) return;
                        visible ? openPopover(name) : closePopover(name);
                      }}
                      content={
                        <TaxRateSelectorContent
                          onCancel={() => closePopover(name)}
                          onApply={(selectedIds) =>
                            handleTaxApply(name, selectedIds, () =>
                              closePopover(name),
                            )
                          }
                          taxRates={rates}
                          currentTaxIds={currentTaxIds}
                        />
                      }
                    >
                      <Button
                        type={currentTaxIds.length > 0 ? "primary" : "default"}
                        size="small"
                        loading={loadingTaxRates}
                        className={`tax-rate-button flex items-center justify-center min-w-[44px] ${isTaxSelectorDisabled
                          ? "tax-rate-button--disabled"
                          : ""
                          }`}
                      >
                        {<Percent size={14} />}
                        {currentTaxIds.length > 0 && (
                          <span>({currentTaxIds.length})</span>
                        )}
                      </Button>
                    </Popover>

                    {/* Remove Button */}
                    <Button
                      icon={<XCircle size={18} className="text-red-500" />}
                      onClick={() => remove(name)}
                      type="text"
                      className="flex items-center justify-center flex-shrink-0"
                      style={{ height: "auto", padding: 0 }}
                    />
                  </div>

                  {/* Applied Taxes */}
                  <Form.Item
                    {...restField}
                    name={[name, "appliedTaxes"]}
                    hidden
                  >
                    <Input />
                  </Form.Item>
                </div>
              );
            })}

            <div className="grid grid-cols-12">
              <Form.Item className="mt-4 mb-0 col-span-10">
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      itemName: "",
                      itemDescription: null,
                      quantity: 1,
                      appliedTaxes: [],
                    })
                  }
                  block
                  icon={<PlusCircle size={16} />}
                  className="flex items-center justify-center gap-2"
                >
                  Add Item
                </Button>
              </Form.Item>
            </div>
          </>
        )}
      </Form.List>
    </div>
  );
};
